import React, { useCallback, useMemo, useRef } from 'react';
import { Handle, Position } from 'reactflow';
import useStore from '../store/useStore';
import TypeAutocomplete from './TypeAutocomplete';

export default function EntityNode({ id, data }) {
  const updateNodeData = useStore((s) => s.updateNodeData);
  const updateField = useStore((s) => s.updateField);
  const readOnly = useStore((s) => s.readOnly);
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const selectedFieldIndex = useStore((s) => s.selectedFieldIndex);
  const setSelectedNode = useStore((s) => s.setSelectedNode);
  const editingCell = useStore((s) => s.editingCell);
  const setEditingCell = useStore((s) => s.setEditingCell);
  const clearEditing = useStore((s) => s.clearEditing);
  const swapFields = useStore((s) => s.swapFields);
  const edges = useStore((s) => s.edges);

  const dragIndexRef = useRef(null);
  const isSelected = selectedNodeId === id;

  // Indices of fields that have at least one connected edge (relation locked).
  const linkedFieldIndices = useMemo(() => {
    const set = new Set();
    for (const e of edges) {
      if (e.source === id && typeof e.sourceHandle === 'string') {
        const m = e.sourceHandle.match(/^field-(\d+)/);
        if (m) set.add(parseInt(m[1], 10));
      }
      if (e.target === id && typeof e.targetHandle === 'string') {
        const m = e.targetHandle.match(/^field-(\d+)/);
        if (m) set.add(parseInt(m[1], 10));
      }
    }
    return set;
  }, [edges, id]);

  const isEditing = (fieldIndex, part) =>
    editingCell?.nodeId === id && editingCell?.fieldIndex === fieldIndex && editingCell?.part === part;

  const handleFieldClick = useCallback(
    (e, fieldIndex) => {
      e.stopPropagation();
      setSelectedNode(id, fieldIndex);
    },
    [id, setSelectedNode]
  );

  const handleDoubleClick = useCallback(
    (e, fieldIndex, part) => {
      if (readOnly) return;
      // Type is locked while a relation edge is connected to this row.
      if (part === 'type' && linkedFieldIndices.has(fieldIndex)) return;
      e.stopPropagation();
      setEditingCell({ nodeId: id, fieldIndex, part });
    },
    [id, setEditingCell, readOnly, linkedFieldIndices]
  );

  const handleToggleConstraint = useCallback(
    (fieldIndex, constraint) => {
      if (readOnly) return;
      const field = data.fields[fieldIndex];
      updateField(id, fieldIndex, { [constraint]: !field[constraint] });
    },
    [id, data.fields, updateField, readOnly]
  );

  // Drag & drop reorder
  const handleDragStart = (e, index) => {
    if (readOnly) { e.preventDefault(); return; }
    if (data.fields[0] && index === 0) { e.preventDefault(); return; }
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ''); // required for firefox
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e) => {
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');
    const fromIndex = dragIndexRef.current;
    if (fromIndex !== null && fromIndex !== toIndex) {
      swapFields(id, fromIndex, toIndex);
    }
    dragIndexRef.current = null;
  };

  return (
    <div
      className={`entity-node ${isSelected ? 'selected' : ''}`}
      onClick={() => setSelectedNode(id)}
    >
      <div
        className="entity-header"
        onDoubleClick={(e) => handleDoubleClick(e, null, 'tableName')}
      >
        {isEditing(null, 'tableName') ? (
          <textarea
            className="entity-name-input nodrag"
            defaultValue={data.name}
            autoFocus
            onBlur={(e) => {
              updateNodeData(id, { name: e.target.value });
              clearEditing();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
            }}
            onFocus={(e) => { e.target.select(); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
            onChange={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
            spellCheck={false}
            rows={1}
          />
        ) : (
          <div className="entity-name-display">{data.name}</div>
        )}
      </div>
      <div className="entity-fields">
        {data.fields.map((field, index) => {
          const isFieldSelected = isSelected && selectedFieldIndex === index;
          const isIdField = index === 0;
          return (
            <div
              key={index}
              className={`entity-field-row ${isFieldSelected ? 'field-selected nodrag' : ''} ${index % 2 === 1 ? 'alt' : ''}`}
              onClick={(e) => handleFieldClick(e, index)}
              draggable={isFieldSelected && !isIdField}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
            >
              <Handle type="source" position={Position.Right} id={`field-${index}-right`}
                className="field-handle field-handle-right" style={{ position: 'absolute', right: -6 }} />
              <Handle type="source" position={Position.Left} id={`field-${index}-left`}
                className="field-handle field-handle-left" style={{ position: 'absolute', left: -6 }} />
              <div
                className={`field-type-cell${linkedFieldIndices.has(index) ? ' linked' : ''}`}
                onDoubleClick={(e) => !isIdField && handleDoubleClick(e, index, 'type')}
                title={linkedFieldIndices.has(index) ? 'Type locked — remove the relation to change' : undefined}
              >
                {isEditing(index, 'type') ? (
                  <TypeAutocomplete
                    value={field.type}
                    onChange={(val) => {
                      updateField(id, index, { type: val });
                      clearEditing();
                    }}
                    nodeId={id}
                    autoOpen
                  />
                ) : (
                  <span className="field-type-display">{field.type}</span>
                )}
              </div>
              <div
                className="field-name-cell"
                onDoubleClick={(e) => !isIdField && handleDoubleClick(e, index, 'name')}
              >
                {isEditing(index, 'name') ? (
                  <textarea
                    className="field-name-input nodrag"
                    defaultValue={field.name}
                    autoFocus
                    onBlur={(e) => {
                      updateField(id, index, { name: e.target.value });
                      clearEditing();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
                    }}
                    onFocus={(e) => { e.target.select(); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    onChange={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    placeholder="field_name"
                    spellCheck={false}
                    rows={1}
                  />
                ) : (
                  <span className={`field-name-display ${isIdField ? 'id-field' : ''}`}>
                    {field.name || <em className="placeholder-text">field_name</em>}
                  </span>
                )}
              </div>
              <div className="field-constraints">
                {!isIdField && (
                  <>
                    <button
                      className={`constraint-btn ${!field.nullable ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleConstraint(index, 'nullable');
                      }}
                      title="Not Null"
                    >
                      N
                    </button>
                    <button
                      className={`constraint-btn ${field.unique ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleConstraint(index, 'unique');
                      }}
                      title="Unique"
                    >
                      U
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
